import { useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { LineChart } from '../../charts/LineChart';
import { Sparkline } from '../../charts/Sparkline';
import {
  Card,
  ChartCard,
  DataTable,
  Drawer,
  DrawerSection,
  KeyValues,
  Stat,
  StatusChip,
  type Column,
} from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatByUnit, num, pct, periodLabel, snakeLabel } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Role } from '../../lib/types';
import {
  KPI_BANDS,
  type KpiHistoryData,
  type KpiOption,
  type KpiResultRow,
  type KpiRow,
  type ScorecardData,
  type ScorecardMeasure,
} from './adminTypes';
import { RegisterEmpty, RegisterPanel, type RegisterSpec } from './adminUi';

const KPI_CATEGORY = [
  'calls', 'procurement', 'depot', 'contracts', 'registrations', 'vendors', 'people', 'admin',
] as const;
const KPI_UNIT = ['percent', 'count', 'days', 'hours', 'seconds', 'currency', 'score'] as const;
const KPI_FREQUENCY = ['daily', 'weekly', 'monthly', 'quarterly'] as const;
const DIRECTION = ['higher', 'lower'] as const;

/** The health convention the standalone app drew its target line at. */
const HEALTH_TARGET = 85;

type Sub = 'scorecard' | 'measures' | 'results';

/**
 * The KPI scorecard — how the admin function is measured.
 *
 * A CATALOGUE, not a fixed report, and that is what separates it from
 * `coo.ServiceScorecard`. That one is the service desk's six numbers for a month,
 * a column per measure, because those six ARE the report and adding a seventh is
 * a deliberate act. Here the measure is a row: the admin function can add
 * "average quote turnaround" without a migration, and each measure carries its
 * own target, floor, direction and weight.
 *
 * The two are not allowed to overlap. The categories here are the admin
 * function's own and deliberately exclude service-desk SLA and CSAT, which
 * already have one home. Two places recording CSAT would eventually disagree,
 * and the one nobody was looking at would be the one quoted in a board pack.
 *
 * Three things about the scoring are worth knowing, and all three come from
 * `lib/admin/kpiScore.ts` on the server so this page and the dashboard tile can
 * never disagree:
 *
 *   - The period defaults to the last COMPLETE month. A month still running is
 *     always behind target, and a scorecard that is red for the first three weeks
 *     of every month is one nobody opens.
 *   - `no_data` is a status, not an absence. A measure nobody entered counts
 *     toward neither side of the health fraction, so a half-entered month reads
 *     as incomplete rather than as a bad month.
 *   - Direction is per measure. On speed to answer, twelve against a target of
 *     twenty is a hit, and attainment is inverted so 100% always means "made it".
 */
export function AdminScorecard({
  canWrite,
  onChanged,
}: {
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [sub, setSub] = useState<Sub>('scorecard');
  const [period, setPeriod] = useState<string | null>(null);
  const [drillIn, setDrillIn] = useState<number | null>(null);

  const analytics = useResource<ScorecardData>(
    `/admin/analytics/scorecard${period ? `?period=${period}` : ''}`,
  );
  const roles = useResource<Role[]>('/roles');
  const kpiOptions = useResource<KpiOption[]>('/admin/pickers/kpis');

  const measureSpec = useMemo<RegisterSpec<KpiRow>>(
    () => ({
      resource: 'kpis',
      title: 'The measures',
      singular: 'Measure',
      emptyTitle: 'No measures defined yet',
      emptyBody:
        'Define what the admin function is judged on. Each measure carries a target, an optional floor, and whether beating it means a bigger or a smaller number — without that last field, "speed to answer: 12 against a target of 20" would read as a miss.',
      searchHint: 'Code, name or accountable role',
      idOf: (row) => row.AdminKpiId,
      labelOf: (row) => `${row.KpiCode} — ${row.Name}`,
      filters: [
        { param: 'category', label: 'Category', options: KPI_CATEGORY },
        { param: 'unit', label: 'Unit', options: KPI_UNIT },
        { param: 'frequency', label: 'Frequency', options: KPI_FREQUENCY },
      ],
      columns: [
        {
          key: 'KpiCode',
          label: 'Measure',
          render: (row) => (
            <div>
              <div className="admin-cell-title">
                {row.KpiCode} — {row.Name}
              </div>
              <div className="xs muted">
                {snakeLabel(row.Category)} · {snakeLabel(row.Frequency)}
                {row.DataSource ? ` · from ${row.DataSource}` : ''}
                {row.IsActive ? '' : ' · inactive'}
              </div>
            </div>
          ),
        },
        {
          key: 'Target',
          label: 'Target',
          align: 'right',
          render: (row) => (
            <div>
              <div>{formatByUnit(row.Target, row.Unit)}</div>
              <div className="xs muted">
                {row.Direction === 'lower' ? 'lower is better' : 'higher is better'}
              </div>
            </div>
          ),
        },
        {
          key: 'FloorValue',
          label: 'Floor',
          align: 'right',
          render: (row) =>
            row.FloorValue === null ? (
              /* Said explicitly, because the fallback is a convention rather than
                 a measurement — see scoreKpi. */
              <span className="sub">±10%</span>
            ) : (
              formatByUnit(row.FloorValue, row.Unit)
            ),
        },
        { key: 'Weight', label: 'Weight', align: 'right', render: (row) => num(row.Weight, 2) },
        {
          key: 'OwnerRoleName',
          label: 'Accountable role',
          render: (row) => row.OwnerRoleName ?? <span className="sub">—</span>,
        },
        {
          key: 'ResultCount',
          label: 'Results',
          align: 'right',
          render: (row) =>
            row.ResultCount === 0 ? <span className="admin-warn-text">none</span> : num(row.ResultCount),
        },
      ],
      fields: [
        { name: 'kpiCode', label: 'Code', kind: 'text' },
        { name: 'category', label: 'Category', kind: 'select', options: KPI_CATEGORY },
        { name: 'name', label: 'Name', kind: 'text', wide: true },
        { name: 'description', label: 'What it measures', kind: 'textarea', wide: true },
        { name: 'unit', label: 'Unit', kind: 'select', options: KPI_UNIT },
        {
          name: 'direction',
          label: 'Better when',
          kind: 'select',
          options: DIRECTION,
          hint: 'Whether beating the target means a bigger number or a smaller one.',
        },
        { name: 'target', label: 'Target', kind: 'decimal' },
        {
          name: 'floorValue',
          label: 'Floor',
          kind: 'decimal',
          hint: 'The red line. Leave blank to treat a 10% miss as at-risk instead.',
        },
        { name: 'frequency', label: 'Frequency', kind: 'select', options: KPI_FREQUENCY },
        {
          name: 'weight',
          label: 'Weight',
          kind: 'decimal',
          hint: 'Must be above zero — deactivate a measure rather than weighting it nothing.',
        },
        {
          name: 'ownerRoleId',
          label: 'Accountable role',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          hint: 'A role, not a person — a measure outlives whoever holds the seat.',
          choices: (roles.data ?? [])
            .filter((r) => r.IsActive)
            .map((r) => ({ value: String(r.RoleId), label: r.Title })),
        },
        { name: 'dataSource', label: 'Where the number comes from', kind: 'text', wide: true },
        { name: 'isActive', label: 'Active', kind: 'checkbox' },
      ],
      blank: {
        kpiCode: '',
        category: 'admin',
        name: '',
        description: '',
        unit: 'percent',
        direction: 'higher',
        target: '',
        floorValue: '',
        frequency: 'monthly',
        weight: 1,
        ownerRoleId: '',
        dataSource: '',
        isActive: true,
      },
      toForm: (row) => ({
        kpiCode: row.KpiCode,
        category: row.Category,
        name: row.Name,
        description: row.Description ?? '',
        unit: row.Unit,
        direction: row.Direction,
        target: row.Target,
        floorValue: row.FloorValue ?? '',
        frequency: row.Frequency,
        weight: row.Weight,
        ownerRoleId: row.OwnerRoleId ?? '',
        dataSource: row.DataSource ?? '',
        isActive: row.IsActive,
      }),
    }),
    [roles.data],
  );

  const resultSpec = useMemo<RegisterSpec<KpiResultRow>>(
    () => ({
      resource: 'kpi-results',
      title: 'Recorded results',
      singular: 'Result',
      emptyTitle: 'No results recorded yet',
      emptyBody:
        'One number per measure per month. The scorecard scores whatever is here and reports the rest as "no data" rather than as a miss, so entering a month partially is safe.',
      searchHint: 'Measure or commentary',
      idOf: (row) => row.AdminKpiResultId,
      labelOf: (row) => `${row.KpiCode} — ${row.Period}`,
      columns: [
        { key: 'Period', label: 'Period', render: (row) => periodLabel(row.Period) },
        {
          key: 'KpiCode',
          label: 'Measure',
          render: (row) => (
            <div>
              <div className="admin-cell-title">{row.KpiName}</div>
              <div className="xs muted">{row.KpiCode}</div>
            </div>
          ),
        },
        {
          key: 'Actual',
          label: 'Actual',
          align: 'right',
          render: (row) => formatByUnit(row.Actual, row.Unit),
        },
        {
          key: 'Target',
          label: 'Target',
          align: 'right',
          render: (row) => formatByUnit(row.Target, row.Unit),
        },
        {
          key: 'Commentary',
          label: 'Commentary',
          render: (row) => row.Commentary ?? <span className="sub">—</span>,
        },
      ],
      fields: [
        {
          name: 'adminKpiId',
          label: 'Measure',
          kind: 'select',
          choices: (kpiOptions.data ?? []).map((k) => ({
            value: String(k.AdminKpiId),
            label: `${k.KpiCode} — ${k.Name}`,
          })),
        },
        { name: 'period', label: 'Period', kind: 'period', hint: 'The month being reported.' },
        { name: 'actual', label: 'Actual', kind: 'decimal' },
        { name: 'commentary', label: 'Commentary', kind: 'textarea', wide: true },
      ],
      blank: { adminKpiId: '', period: '', actual: '', commentary: '' },
      toForm: (row) => ({
        adminKpiId: row.AdminKpiId,
        period: row.Period,
        actual: row.Actual,
        commentary: row.Commentary ?? '',
      }),
    }),
    [kpiOptions.data],
  );

  const reload = () => {
    analytics.reload();
    kpiOptions.reload();
    onChanged();
  };

  const d = analytics.data;

  return (
    <div className="admin-stack">
      <div className="ui-segmented" role="group" aria-label="Scorecard view">
        <button type="button" aria-pressed={sub === 'scorecard'} onClick={() => setSub('scorecard')}>
          Scorecard
        </button>
        <button type="button" aria-pressed={sub === 'measures'} onClick={() => setSub('measures')}>
          Measures
        </button>
        <button type="button" aria-pressed={sub === 'results'} onClick={() => setSub('results')}>
          Enter results
        </button>
      </div>

      {sub === 'measures' && <RegisterPanel spec={measureSpec} canWrite={canWrite} onChanged={reload} />}
      {sub === 'results' && <RegisterPanel spec={resultSpec} canWrite={canWrite} onChanged={reload} />}

      {sub === 'scorecard' && (
        <>
          {analytics.error && <ErrorBanner message={analytics.error} />}
          {!d && analytics.loading && <Loading label="Loading the scorecard" />}

          {d && d.measures.length === 0 && (
            <RegisterEmpty
              title="No measures defined"
              action={
                canWrite ? (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setSub('measures')}>
                    Define the first measure
                  </button>
                ) : undefined
              }
            >
              <p>
                This is where the admin function&rsquo;s own targets live — quote turnaround, receiving
                accuracy, contract renewal rate, SOP currency, whatever this team is judged on. Each
                measure carries a target, an optional floor, a direction and a weight, and the
                scorecard rolls them into one weighted health figure per month.
              </p>
              <p className="mt-2">
                It is deliberately not the service desk&rsquo;s scorecard. CSAT and the SLA percentages
                already have one home under Service Delivery, and recording them in two places would
                guarantee the two eventually disagree.
              </p>
            </RegisterEmpty>
          )}

          {d && d.measures.length > 0 && (
            <>
              <div className="row-between wrap">
                <p className="sub">
                  {periodLabel(d.period)} — the last complete month by default, because a month still
                  running is always behind target.
                </p>
                <div className="row wrap">
                  <label className="admin-inline-label" htmlFor="scorecard-period">
                    Period
                  </label>
                  <input
                    id="scorecard-period"
                    className="input"
                    type="month"
                    value={period ?? d.period}
                    onChange={(e) => setPeriod(e.target.value || null)}
                  />
                </div>
              </div>

              <div className="grid grid-4">
                <Stat
                  label="Weighted health"
                  value={d.summary.weightedHealth === null ? '—' : pct(d.summary.weightedHealth, 0)}
                  foot={
                    d.summary.weightedHealth === null
                      ? 'nothing recorded for this month'
                      : `${d.summary.measured} of ${d.summary.total} measured`
                  }
                />
                <Stat label="On target" value={d.summary.onTarget} foot="at or beyond target" />
                <Stat
                  label="At risk"
                  value={d.summary.atRisk}
                  foot="past target, inside the floor"
                />
                <Stat
                  label="Off target"
                  value={d.summary.offTarget}
                  flagged={d.summary.offTarget > 0}
                  foot="past the floor"
                />
              </div>

              {d.summary.noData > 0 && (
                <p className="sub">
                  {num(d.summary.noData)}{' '}
                  {d.summary.noData === 1 ? 'measure has' : 'measures have'} no result for{' '}
                  {periodLabel(d.period)}. They count toward neither side of the health figure — an
                  unentered month reads as incomplete rather than as a bad month.
                </p>
              )}

              <div className="admin-split">
                <ChartCard
                  title="Health over six months"
                  subtitle="Weighted, ending on the month shown."
                  table={{
                    columns: TREND_COLUMNS,
                    rows: d.trend,
                    rowKey: (row) => row.period,
                  }}
                >
                  <LineChart
                    data={d.trend}
                    xKey="period"
                    series={[{ key: 'weightedHealth', label: 'Weighted health', color: 'var(--series-1)' }]}
                    target={{ value: HEALTH_TARGET, label: `Target ${HEALTH_TARGET}%` }}
                    xFormat={periodLabel}
                    yFormat={(v) => `${Math.round(v)}%`}
                    zeroBased
                    area
                    height={240}
                    emptyMessage="No results recorded in the last six months."
                  />
                </ChartCard>

                <ChartCard
                  title="Where the misses are"
                  subtitle="Measures by status, per month."
                  table={{
                    columns: TREND_COLUMNS,
                    rows: d.trend,
                    rowKey: (row) => row.period,
                  }}
                >
                  <BarChart
                    data={d.trend}
                    xKey="period"
                    stacked
                    /* Status data, so the status ladder is correct here rather
                       than the categorical slots — the colour IS the meaning. */
                    series={[
                      { key: 'onTarget', label: 'On target', color: 'var(--status-good)' },
                      { key: 'atRisk', label: 'At risk', color: 'var(--status-warning)' },
                      { key: 'offTarget', label: 'Off target', color: 'var(--status-critical)' },
                    ]}
                    xFormat={periodLabel}
                    yFormat={(v) => num(v)}
                    height={240}
                  />
                </ChartCard>
              </div>

              <Card title="By category" subtitle="Which part of the admin function is carrying the misses.">
                <DataTable
                  columns={CATEGORY_COLUMNS}
                  rows={d.categories}
                  rowKey={(row) => row.category}
                />
              </Card>

              <Card
                title={`Every measure — ${periodLabel(d.period)}`}
                subtitle="Click a row for its full history. Attainment is inverted for a lower-is-better measure, so 100% always means the target was met."
              >
                <DataTable
                  columns={measureColumns(setDrillIn)}
                  rows={d.measures}
                  rowKey={(row) => String(row.adminKpiId)}
                />
              </Card>
            </>
          )}
        </>
      )}

      {drillIn !== null && <KpiDrillIn kpiId={drillIn} onClose={() => setDrillIn(null)} />}
    </div>
  );
}

const TREND_COLUMNS: Column<ScorecardData['trend'][number]>[] = [
  { key: 'period', label: 'Month', render: (row) => periodLabel(row.period) },
  { key: 'measured', label: 'Measured', align: 'right' },
  { key: 'onTarget', label: 'On target', align: 'right' },
  { key: 'atRisk', label: 'At risk', align: 'right' },
  { key: 'offTarget', label: 'Off target', align: 'right' },
  {
    key: 'weightedHealth',
    label: 'Health',
    align: 'right',
    render: (row) => (row.weightedHealth === null ? '—' : pct(row.weightedHealth, 0)),
  },
];

const CATEGORY_COLUMNS: Column<ScorecardData['categories'][number]>[] = [
  { key: 'category', label: 'Category', render: (row) => snakeLabel(row.category) },
  { key: 'count', label: 'Measures', align: 'right' },
  { key: 'onTarget', label: 'On target', align: 'right' },
  { key: 'atRisk', label: 'At risk', align: 'right' },
  {
    key: 'offTarget',
    label: 'Off target',
    align: 'right',
    render: (row) => (
      <span className={row.offTarget > 0 ? 'admin-warn-text' : undefined}>{row.offTarget}</span>
    ),
  },
  {
    key: 'noData',
    label: 'No data',
    align: 'right',
    render: (row) => (row.noData > 0 ? <span className="sub">{row.noData}</span> : '—'),
  },
  {
    key: 'weightedHealth',
    label: 'Health',
    align: 'right',
    render: (row) => (row.weightedHealth === null ? '—' : pct(row.weightedHealth, 0)),
  },
];

function measureColumns(onDrillIn: (id: number) => void): Column<ScorecardMeasure>[] {
  return [
    {
      key: 'name',
      label: 'Measure',
      render: (row) => (
        <button type="button" className="admin-link-button" onClick={() => onDrillIn(row.adminKpiId)}>
          <span className="admin-cell-title">{row.name}</span>
          <span className="xs muted">
            {row.kpiCode} · {snakeLabel(row.category)}
            {row.ownerRoleName ? ` · ${row.ownerRoleName}` : ''}
          </span>
        </button>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusChip band={KPI_BANDS[row.status]}>
          {row.status === 'no_data' ? 'not entered' : snakeLabel(row.status)}
        </StatusChip>
      ),
    },
    {
      key: 'actual',
      label: 'Actual',
      align: 'right',
      render: (row) => formatByUnit(row.actual, row.unit),
    },
    {
      key: 'target',
      label: 'Target',
      align: 'right',
      render: (row) => (
        <div>
          <div>{formatByUnit(row.target, row.unit)}</div>
          <div className="xs muted">{row.direction === 'lower' ? 'lower better' : 'higher better'}</div>
        </div>
      ),
    },
    {
      key: 'attainmentPercent',
      label: 'Attainment',
      align: 'right',
      render: (row) => (row.attainmentPercent === null ? '—' : pct(row.attainmentPercent, 0)),
    },
    {
      key: 'delta',
      label: 'vs previous',
      align: 'right',
      render: (row) => {
        if (row.delta === null) return <span className="sub">—</span>;
        /* `improving` is the server's answer, not the sign of the delta: on a
           lower-is-better measure, down IS better. */
        return (
          <span className={row.improving === false ? 'admin-warn-text' : undefined}>
            <span aria-hidden="true">{row.delta === 0 ? '→' : row.improving ? '▲' : '▼'}</span>{' '}
            {formatByUnit(Math.abs(row.delta), row.unit)}
          </span>
        );
      },
    },
    {
      key: 'weight',
      label: 'Weight',
      align: 'right',
      render: (row) => num(row.weight, 2),
    },
  ];
}

/**
 * One measure's whole recorded history.
 *
 * Scored against the measure's CURRENT target, which is a real caveat and is
 * stated on the panel: re-targeting a measure re-colours its past. The
 * alternative — snapshotting the target onto every result — would be honest about
 * history but would then answer "how are we doing against the target we set" with
 * last year's answer.
 */
function KpiDrillIn({ kpiId, onClose }: { kpiId: number; onClose: () => void }) {
  const history = useResource<KpiHistoryData>(`/admin/analytics/scorecard/${kpiId}/history`);
  const d = history.data;

  return (
    <Drawer
      open
      onClose={onClose}
      title={d ? d.definition.name : 'Measure'}
      subtitle={d ? `${d.definition.kpiCode} · ${snakeLabel(d.definition.category)}` : undefined}
    >
      {history.error && <ErrorBanner message={history.error} />}
      {!d && history.loading && <Loading label="Loading history" />}

      {d && (
        <>
          {d.definition.description && <p>{d.definition.description}</p>}

          <DrawerSection title="The measure">
            <KeyValues
              items={[
                ['Target', formatByUnit(d.definition.target, d.definition.unit)],
                [
                  'Floor',
                  d.definition.floorValue === null
                    ? '±10% of target (no floor set)'
                    : formatByUnit(d.definition.floorValue, d.definition.unit),
                ],
                ['Better when', d.definition.direction === 'lower' ? 'lower' : 'higher'],
                ['Frequency', snakeLabel(d.definition.frequency)],
                ['Weight', num(d.definition.weight, 2)],
                ['Accountable role', d.definition.ownerRoleName ?? '—'],
                ['Data source', d.definition.dataSource ?? '—'],
              ]}
            />
          </DrawerSection>

          <DrawerSection title="History" count={`${d.history.length}`}>
            {d.history.length === 0 ? (
              <p className="sub">No results have been recorded for this measure yet.</p>
            ) : (
              <>
                <LineChart
                  data={d.history}
                  xKey="period"
                  series={[{ key: 'actual', label: d.definition.name, color: 'var(--series-1)' }]}
                  target={{ value: d.definition.target, label: 'Target' }}
                  xFormat={periodLabel}
                  yFormat={(v) => formatByUnit(v, d.definition.unit)}
                  area
                  height={210}
                />
                <p className="sub mt-2">
                  Every point is scored against the target as it stands today. Changing the target
                  re-colours this chart.
                </p>
                <DataTable
                  columns={[
                    { key: 'period', label: 'Month', render: (row) => periodLabel(row.period) },
                    {
                      key: 'actual',
                      label: 'Actual',
                      align: 'right',
                      render: (row) => formatByUnit(row.actual, d.definition.unit),
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      render: (row) => (
                        <StatusChip band={KPI_BANDS[row.status]}>{snakeLabel(row.status)}</StatusChip>
                      ),
                    },
                    {
                      key: 'commentary',
                      label: 'Commentary',
                      render: (row) => row.commentary ?? <span className="sub">—</span>,
                    },
                  ]}
                  rows={d.history}
                  rowKey={(row) => row.period}
                />
              </>
            )}
          </DrawerSection>

          {d.history.length > 1 && (
            <DrawerSection title="At a glance">
              <Sparkline
                values={d.history.map((h) => h.actual)}
                target={d.definition.target}
                width={280}
                height={54}
              />
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  );
}
